import type {
  CreateOrderSubscriptionData,
  OrderDetailSummaryType,
  OrderDetailType,
  ProductQuantitiesType,
  RegisterQuantitiesData,
  UpdateOrderStatusResponse
} from 'types/assembly/model'
import type {
  CustomAxiosRequestConfig,
  TransferPageData
} from 'types/commons/model'
import type { RegisterMissingData } from 'types/utils/model'
import { stockTransfersAxiosInstance } from './stock-transfers-api.axios.instance'

export const getTransferRequests = async (
  status?: string[]
): Promise<TransferPageData> => {
  const axios = stockTransfersAxiosInstance()
  const url = status?.length
    ? `api/orders?status=${status.join(',')}&limit=100`
    : 'api/orders?limit=100'
  return await axios.get(url).then((e) => e.data)
}

export const getTransferDetail = async (
  transferId: string
): Promise<OrderDetailSummaryType> => {
  const axios = stockTransfersAxiosInstance()
  return await axios.get(`api/orders/${transferId}`).then((e) => e.data)
}

export const getPreparationContainers = async (
  transferId: string
): Promise<OrderDetailType> => {
  const axios = stockTransfersAxiosInstance()
  return await axios.get(`api/orders/${transferId}`).then((e) => e.data)
}

export const getTransferByContainer = async (
  containerId: string
): Promise<{ transferId: string; docNum: string }> => {
  const axios = stockTransfersAxiosInstance()
  return await axios
    .get(`api/orders/containers/${containerId}`)
    .then((e) => e.data)
}

export const getContainersProducts = async (
  transferId: string,
  containers: string
): Promise<OrderDetailType> => {
  const axios = stockTransfersAxiosInstance()
  const url = `api/orders/${transferId}?containers=${containers}`
  return await axios.get(url).then((e) => e.data)
}

export const getProductQuantities = async (
  transferId: string,
  ean: string
): Promise<ProductQuantitiesType> => {
  const axios = stockTransfersAxiosInstance()
  const options: CustomAxiosRequestConfig = { skipErrorAlert: true }
  const url = `api/orders/${transferId}/product/${ean}`
  return await axios.get(url, options).then((e) => e.data)
}

export const updateQuantities = async (
  transferId: string,
  data: RegisterQuantitiesData
): Promise<RegisterQuantitiesData> => {
  const axios = stockTransfersAxiosInstance()
  const url = `api/orders/${transferId}/product/update`
  return await axios.patch(url, data).then((e) => e.data)
}

export const updateOrderStatus = async (
  transferId: string,
  orderStatus: string
): Promise<UpdateOrderStatusResponse> => {
  const axios = stockTransfersAxiosInstance()
  const url = `api/orders/${transferId}/status/update`
  return await axios.patch(url, { orderStatus }).then((e) => e.data)
}

// TODO: create interface for response shape
export const shipContainers = async (
  transferId: string,
  containers: string[]
): Promise<any> => {
  const axios = stockTransfersAxiosInstance()
  const url = `api/orders/${transferId}/containers/ship`
  return await axios.post(url, { containers }).then((e) => e.data)
}

// TODO: create interface for response shape
export const registerMissingRequest = async (
  data: RegisterMissingData
): Promise<any> => {
  const axios = stockTransfersAxiosInstance()
  const url = `api/orders/${data.transferId}/product/missing_waste`
  return await axios.post(url, data).then((e) => e.data)
}

export const subscribeToOrderChanges = async (
  data: CreateOrderSubscriptionData
): Promise<{ data: string; subscriptionId: string }> => {
  const axios = stockTransfersAxiosInstance()
  const url = 'api/orders/subscribe'
  return await axios.post(url, data).then((e) => e.data)
}

export const unsubscribeToOrderChanges = async (
  subscriptionId: string
): Promise<{ acknowledged: boolean; deletedCount: number }> => {
  const axios = stockTransfersAxiosInstance()
  const url = 'api/orders/desubscribe'
  return await axios.post(url, { id: subscriptionId }).then((e) => e.data)
}

export const moveContainer = async (
  transferId: string,
  containerId: string,
  location: string
): Promise<{
  previousLocation: string
  currentLocation: string
}> => {
  const axios = stockTransfersAxiosInstance()
  const options: CustomAxiosRequestConfig = { skipErrorAlert: true }
  const url = `api/orders/${transferId}/containers/move`
  return await axios
    .patch(url, { containerId, location }, options)
    .then((e) => e.data)
}
